import React from 'react';
import { Typography, Divider, Box } from '@mui/material';
import styled from 'styled-components';

const BlogTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 1rem;
`;

const BlogDate = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const BlogSection = styled(Box)`
  margin: 2rem 0;
`;

const ImageContainer = styled(Box)`
  margin: 2rem 0;
  text-align: center;
`;

const ImageCaption = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const HeroImage = styled(Box)`
  width: 100%;
  margin-bottom: 2rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const ColdWalletSetup: React.FC = () => {
  return (
    <>
      <BlogTitle variant="h4">
        How to Set Up a Cold Wallet for Chia (XCH) - Part 1
      </BlogTitle>
      <BlogDate>March 15, 2024</BlogDate>
      <Divider sx={{ my: 2 }} />

      <HeroImage>
        <Box
          component="img"
          src="/post/cold-key.webp"
          alt="Secure Cold Wallet Setup for Chia (XCH)"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
      </HeroImage>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          Introduction
        </Typography>
        <Typography>
          In this comprehensive guide series, we'll walk you through everything you need to know about creating 
          and managing a cold wallet for your Chia (XCH) assets. Part 1 focuses on understanding cold wallets 
          and preparing for the setup process. Whether you're new to cryptocurrency security or an experienced 
          holder, this guide will help you implement best-in-class security practices for your XCH.
        </Typography>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          What is a Cold Wallet?
        </Typography>
        <Typography>
          A cold wallet is a cryptocurrency wallet that is completely offline, making it immune to online threats 
          like hacking and malware. For Chia (XCH), this means your private keys are generated on a 
          device that has never been connected to the internet. This provides the highest level of security for 
          your digital assets.
        </Typography>
        
        <ImageContainer>
          <Box
            component="img"
            src="/images/cold-wallet-setup.jpg"
            alt="Cold Wallet Setup Process"
            sx={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            }}
          />
          <ImageCaption>
            A secure cold wallet setup showing an offline computer generating 24-word seed phrases, 
            which are then stored on paper and metal plates for maximum security.
          </ImageCaption>
        </ImageContainer>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          Why Use a Cold Wallet for Chia (XCH)?
        </Typography>
        <Typography>
          Cold wallets are essential for protecting your Chia (XCH) assets from digital threats. Here's why they're important:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mt: 2 }}>
          <li>
            <Typography>
              <strong>Maximum Security:</strong> Your private keys never touch an internet-connected device
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Protection from Hacks:</strong> Immune to online attacks and malware
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Full Control:</strong> You maintain complete control over your assets without relying on third-party custody
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Long-term Storage:</strong> Perfect for holding significant amounts of XCH securely
            </Typography>
          </li>
        </Box>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          Types of Cold Wallets for Chia
        </Typography>
        <Typography>
          Before setting up your cold wallet, it's important to understand the different options available:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mt: 2 }}>
          <li>
            <Typography>
              <strong>Hardware Wallets:</strong> Dedicated devices like Tangem Cards that store private keys securely 
              with advanced encryption. These are the most user-friendly option for cold storage.
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Paper Wallets:</strong> Physical copies of your private keys printed on paper. While simple, 
              they require careful handling and proper backup procedures.
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Metal Wallets:</strong> Premium solution using durable metal plates for long-term storage. 
              Resistant to fire, water, and physical damage.
            </Typography>
          </li>
        </Box>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          Preparing for Cold Wallet Setup
        </Typography>
        <Typography gutterBottom>
          Before creating your cold wallet, you'll need to gather the following:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mt: 2 }}>
          <li>
            <Typography gutterBottom>
              A clean, offline computer or bootable USB drive with Linux
            </Typography>
          </li>
          <li>
            <Typography gutterBottom>
              Secure storage medium (USB drive, paper, or metal wallet)
            </Typography>
          </li>
          <li>
            <Typography gutterBottom>
              Backup materials (multiple USB drives or paper copies)
            </Typography>
          </li>
          <li>
            <Typography gutterBottom>
              A secure location for storing your backup
            </Typography>
          </li>
        </Box>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          Security Best Practices
        </Typography>
        <Typography gutterBottom>
          Before proceeding with the setup, consider these essential security practices:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mt: 2 }}>
          <li>
            <Typography gutterBottom>Never use an internet-connected device for key generation</Typography>
          </li>
          <li>
            <Typography gutterBottom>Implement a multi-location backup strategy</Typography>
          </li>
          <li>
            <Typography gutterBottom>Use strong encryption for digital backups</Typography>
          </li>
          <li>
            <Typography gutterBottom>Consider using a dedicated offline computer</Typography>
          </li>
          <li>
            <Typography gutterBottom>Document your backup and recovery procedures</Typography>
          </li>
        </Box>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          What's Next?
        </Typography>
        <Typography>
          In Part 2 of this series, we'll guide you through the actual process of creating your cold wallet, 
          including step-by-step instructions for generating and securing your private keys. We'll also cover 
          how to verify your setup and perform initial testing with small amounts of XCH.
        </Typography>
      </BlogSection>

      <BlogSection>
        <Typography variant="h6" gutterBottom>
          Stay Connected with XCH.live
        </Typography>
        <Typography>
          While your cold wallet keeps your private keys offline, XCH.live provides comprehensive monitoring 
          capabilities for your Chia holdings. Our platform allows you to track your wallet's activity, 
          balances, and transactions without compromising your cold wallet security.
        </Typography>
      </BlogSection>
    </>
  );
};

export default ColdWalletSetup; 